/**
 * Browser-side checkout-session helper.
 *
 * Calls the SAME-ORIGIN proxy at `/api/create-session` instead of the open
 * platform directly. The proxy (a Cloudflare Pages Function in production, a
 * Vite dev middleware locally) holds the merchant API key and forwards the
 * request server-side.
 *
 * Why not call the open platform from here:
 * - CORS: the open platform doesn't allow this page's origin, so a direct
 *   browser fetch is blocked. A same-origin request to our own `/api/*` has no
 *   CORS at all.
 * - Secrets: the API key stays on the server (env vars), never in the browser.
 */

/** Default order amount in MAJOR units (e.g. dollars). */
export const DEFAULT_AMOUNT = 0.01;

/**
 * Create a checkout session via the same-origin proxy.
 *
 * @param {{ amount?: number, items?: {name:string,amount:number,num:number}[],
 *           description?: string, returnUrl?: string, currency?: string,
 *           collectBillingAddress?: boolean, collectShippingAddress?: boolean }} [input]
 *   Forwarded verbatim to the proxy. `amount` is in MAJOR units (default 0.01);
 *   an explicit `items` array wins over `amount`.
 * @returns {Promise<{ sessionId: string, checkoutClientSecret?: string } & Record<string, unknown>>}
 *   The open-platform session data (includes `sessionId`, and for
 *   `sessionType: 'embedded'` sessions a `checkoutClientSecret`).
 */
export async function createSession(input = {}) {
    let res;
    try {
        res = await fetch('/api/create-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input ?? {}),
        });
    } catch (err) {
        throw new Error(`could not reach /api/create-session: ${err?.message ?? 'network error'}`);
    }

    let data;
    try {
        data = await res.json();
    } catch {
        throw new Error(`/api/create-session returned non-JSON (HTTP ${res.status})`);
    }

    if (!res.ok || !data?.sessionId) {
        const err = new Error(data?.error || `create-session failed (HTTP ${res.status})`);
        err.detail = data?.detail ?? data;
        throw err;
    }

    // The proxy returns { sessionId, checkoutClientSecret?, session }. Expose the
    // full session object (with sessionId) to callers, and make sure the
    // top-level `checkoutClientSecret` is present even if the nested session view
    // omits it — the browser SDK needs it for embedded sessions.
    const session = data.session ?? { sessionId: data.sessionId };
    if (data.checkoutClientSecret && session.checkoutClientSecret === undefined) {
        session.checkoutClientSecret = data.checkoutClientSecret;
    }
    return session;
}
